// Settings (§14.4), persisted under `shoveoff.settings.v1`. Percent-valued settings are stored as
// integers in their table range; key bindings live separately (`input/bindings.ts`).
import type { QualityTier } from '../render/Renderer';
import { readJson, STORAGE_KEYS, writeJson } from './storage';

export interface Settings {
  audio: { master: number; music: number; sfx: number; voices: number; crowd: number; muteWhenHidden: boolean };   // volumes 0–100
  graphics: { quality: 'auto' | QualityTier; renderScale: number; tiltShift: boolean; showStats: boolean };          // renderScale 50–100 %
  gameplay: {
    screenShake: number;              // 0–100 %
    reducedMotion: boolean; reducedFlashing: boolean;
    pushGuide: 'auto' | 'on' | 'off';  // auto: on until Bean Level 5
    showDizzyNumbers: boolean; holdToCarry: boolean;
    aimAssist: 'off' | 'low' | 'high';
    mouseAim: boolean;
  };
  controls: {
    pad: { swapJumpGrab: boolean; deadzone: number; rumble: number };   // deadzone 0.10–0.30, rumble 0–100 %
    touch: { layout: 'right' | 'left'; buttonSize: 'S' | 'M' | 'L'; stick: 'floating' | 'fixed'; haptics: boolean };
  };
  accessibility: { colorblind: boolean; highContrastOutlines: boolean; uiScale: number; captions: boolean };          // uiScale 80–140 %
}

export const DEFAULT_SETTINGS: Readonly<Settings> = {
  audio: { master: 80, music: 70, sfx: 80, voices: 80, crowd: 70, muteWhenHidden: true },
  graphics: { quality: 'auto', renderScale: 100, tiltShift: true, showStats: false },
  gameplay: {
    screenShake: 100, reducedMotion: false, reducedFlashing: false, pushGuide: 'auto',
    showDizzyNumbers: true, holdToCarry: false, aimAssist: 'high', mouseAim: true,
  },
  controls: {
    pad: { swapJumpGrab: false, deadzone: 0.18, rumble: 100 },
    touch: { layout: 'right', buttonSize: 'M', stick: 'floating', haptics: true },
  },
  accessibility: { colorblind: false, highContrastOutlines: false, uiScale: 100, captions: false },
};

// ---- Validation and persistence ---------------------------------------------------------------

type Obj = Record<string, unknown>;

function obj(v: unknown): Obj {
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Obj) : {};
}

function bool(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

/** Finite number clamped to [lo, hi]; integers when `step` is 1. */
function num(v: unknown, fallback: number, lo: number, hi: number, step = 1): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return fallback;
  const c = Math.min(hi, Math.max(lo, v));
  return step === 1 ? Math.round(c) : c;
}

function oneOf<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(v as T) ? (v as T) : fallback;
}

const QUALITY_OPTIONS = ['auto', 'high', 'medium', 'low', 'potato'] as const;

/** A full, valid Settings from untrusted JSON: unknown fields dropped, ranges clamped, defaults filled. */
export function sanitizeSettings(raw: unknown): Settings {
  const d = DEFAULT_SETTINGS;
  const r = obj(raw);
  const audio = obj(r.audio);
  const graphics = obj(r.graphics);
  const gameplay = obj(r.gameplay);
  const controls = obj(r.controls);
  const pad = obj(controls.pad);
  const touch = obj(controls.touch);
  const a11y = obj(r.accessibility);
  return {
    audio: {
      master: num(audio.master, d.audio.master, 0, 100),
      music: num(audio.music, d.audio.music, 0, 100),
      sfx: num(audio.sfx, d.audio.sfx, 0, 100),
      voices: num(audio.voices, d.audio.voices, 0, 100),
      crowd: num(audio.crowd, d.audio.crowd, 0, 100),
      muteWhenHidden: bool(audio.muteWhenHidden, d.audio.muteWhenHidden),
    },
    graphics: {
      quality: oneOf(graphics.quality, QUALITY_OPTIONS, d.graphics.quality),
      renderScale: num(graphics.renderScale, d.graphics.renderScale, 50, 100),
      tiltShift: bool(graphics.tiltShift, d.graphics.tiltShift),
      showStats: bool(graphics.showStats, d.graphics.showStats),
    },
    gameplay: {
      screenShake: num(gameplay.screenShake, d.gameplay.screenShake, 0, 100),
      reducedMotion: bool(gameplay.reducedMotion, d.gameplay.reducedMotion),
      reducedFlashing: bool(gameplay.reducedFlashing, d.gameplay.reducedFlashing),
      pushGuide: oneOf(gameplay.pushGuide, ['auto', 'on', 'off'], d.gameplay.pushGuide),
      showDizzyNumbers: bool(gameplay.showDizzyNumbers, d.gameplay.showDizzyNumbers),
      holdToCarry: bool(gameplay.holdToCarry, d.gameplay.holdToCarry),
      aimAssist: oneOf(gameplay.aimAssist, ['off', 'low', 'high'], d.gameplay.aimAssist),
      mouseAim: bool(gameplay.mouseAim, d.gameplay.mouseAim),
    },
    controls: {
      pad: {
        swapJumpGrab: bool(pad.swapJumpGrab, d.controls.pad.swapJumpGrab),
        deadzone: num(pad.deadzone, d.controls.pad.deadzone, 0.1, 0.3, 0),
        rumble: num(pad.rumble, d.controls.pad.rumble, 0, 100),
      },
      touch: {
        layout: oneOf(touch.layout, ['right', 'left'], d.controls.touch.layout),
        buttonSize: oneOf(touch.buttonSize, ['S', 'M', 'L'], d.controls.touch.buttonSize),
        stick: oneOf(touch.stick, ['floating', 'fixed'], d.controls.touch.stick),
        haptics: bool(touch.haptics, d.controls.touch.haptics),
      },
    },
    accessibility: {
      colorblind: bool(a11y.colorblind, d.accessibility.colorblind),
      highContrastOutlines: bool(a11y.highContrastOutlines, d.accessibility.highContrastOutlines),
      uiScale: num(a11y.uiScale, d.accessibility.uiScale, 80, 140),
      captions: bool(a11y.captions, d.accessibility.captions),
    },
  };
}

/** Saved settings, or defaults when storage is empty, unavailable or corrupt. */
export function loadSettings(): Settings {
  return sanitizeSettings(readJson(STORAGE_KEYS.settings));
}

export function saveSettings(s: Settings): void {
  writeJson(STORAGE_KEYS.settings, s);
}

// ============================================================================================
// Settings store — live-apply + persistence (§14.4). ONE per page.
//   const settings = createSettingsStore(bus)       // loads `shoveoff.settings.v1`
//   settings.get(): Settings                          current snapshot (replaced, never mutated)
//   settings.set(path, value)                         e.g. set('audio.music', 40) → sanitize,
//   settings.patch({ gameplay: { holdToCarry: true } })   persist, emit 'settings' (everything
//   settings.reset(group?)                            listening applies it live: audio volumes,
//                                                     renderer, input/touch, HUD, UI scale)
//   SETTINGS_SCHEMA: the §14.4 table as data (groups → rows with kind, label, range/options),
//   so the Settings screen can render every row generically. Key rebinding rows are the
//   'bindings' kind (rendered from ../input/bindings KEY_ACTION_ORDER / PAD_ACTION_ORDER).
// ============================================================================================

/** Dotted paths of every leaf setting ('audio.master', 'controls.touch.layout', …). */
export type SettingPath =
  | `audio.${keyof Settings['audio']}`
  | `graphics.${keyof Settings['graphics']}`
  | `gameplay.${keyof Settings['gameplay']}`
  | `controls.pad.${keyof Settings['controls']['pad']}`
  | `controls.touch.${keyof Settings['controls']['touch']}`
  | `accessibility.${keyof Settings['accessibility']}`;

export type SettingValue = number | boolean | string;

export type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] };

/** Reads a leaf setting by path. */
export function getSetting(s: Settings, path: SettingPath): SettingValue {
  let node: unknown = s;
  for (const k of path.split('.')) node = (node as Record<string, unknown>)[k];
  return node as SettingValue;
}

/** A copy of `s` with one leaf replaced, then sanitized (out-of-range values are clamped). */
export function withSetting(s: Settings, path: SettingPath, value: SettingValue): Settings {
  const copy = JSON.parse(JSON.stringify(s)) as Record<string, unknown>;
  const keys = path.split('.');
  let node = copy;
  for (let i = 0; i < keys.length - 1; i++) node = node[keys[i]] as Record<string, unknown>;
  node[keys[keys.length - 1]] = value;
  return sanitizeSettings(copy);
}

function merge(base: unknown, patch: unknown): unknown {
  if (typeof patch !== 'object' || patch === null || Array.isArray(patch)) return patch;
  const out: Record<string, unknown> = { ...(typeof base === 'object' && base !== null ? (base as Record<string, unknown>) : {}) };
  for (const [k, v] of Object.entries(patch)) out[k] = merge(out[k], v);
  return out;
}

export type SettingsGroupId = 'audio' | 'graphics' | 'gameplay' | 'controls' | 'accessibility' | 'profile';

export type SettingRow =
  | { kind: 'slider'; path: SettingPath; label: string; min: number; max: number; step: number; unit: '%' | '' ; hint?: string }
  | { kind: 'toggle'; path: SettingPath; label: string; hint?: string }
  | { kind: 'choice'; path: SettingPath; label: string; options: readonly { value: string; label: string }[]; hint?: string }
  | { kind: 'bindings'; device: 'kbm' | 'pad'; label: string }
  | { kind: 'action'; id: 'resetBindings' | 'resetProgress' | 'editName' | 'testRumble'; label: string; danger?: boolean; hint?: string };

export interface SettingsGroup { id: SettingsGroupId; label: string; rows: readonly SettingRow[] }

/** The §14.4 settings table as data, in display order. */
export const SETTINGS_SCHEMA: readonly SettingsGroup[] = [
  {
    id: 'audio', label: 'Audio', rows: [
      { kind: 'slider', path: 'audio.master', label: 'Master', min: 0, max: 100, step: 5, unit: '%' },
      { kind: 'slider', path: 'audio.music', label: 'Music', min: 0, max: 100, step: 5, unit: '%' },
      { kind: 'slider', path: 'audio.sfx', label: 'Sound effects', min: 0, max: 100, step: 5, unit: '%' },
      { kind: 'slider', path: 'audio.voices', label: 'Voices', min: 0, max: 100, step: 5, unit: '%' },
      { kind: 'slider', path: 'audio.crowd', label: 'Crowd', min: 0, max: 100, step: 5, unit: '%' },
      { kind: 'toggle', path: 'audio.muteWhenHidden', label: 'Mute when tab hidden' },
    ],
  },
  {
    id: 'graphics', label: 'Graphics', rows: [
      {
        kind: 'choice', path: 'graphics.quality', label: 'Quality', options: [
          { value: 'auto', label: 'Auto' }, { value: 'high', label: 'High' }, { value: 'medium', label: 'Medium' },
          { value: 'low', label: 'Low' }, { value: 'potato', label: 'Potato' },
        ],
      },
      { kind: 'slider', path: 'graphics.renderScale', label: 'Render scale', min: 50, max: 100, step: 5, unit: '%' },
      { kind: 'toggle', path: 'graphics.tiltShift', label: 'Tilt-shift' },
      { kind: 'toggle', path: 'graphics.showStats', label: 'Show FPS & ping' },
    ],
  },
  {
    id: 'gameplay', label: 'Gameplay', rows: [
      { kind: 'slider', path: 'gameplay.screenShake', label: 'Screen shake', min: 0, max: 100, step: 10, unit: '%' },
      { kind: 'toggle', path: 'gameplay.reducedMotion', label: 'Reduced motion', hint: 'No shake, zoom punches, slow-mo flourishes or camera sweeps' },
      { kind: 'toggle', path: 'gameplay.reducedFlashing', label: 'Reduced flashing', hint: 'No white flashes or color pulses; softer strobes' },
      {
        kind: 'choice', path: 'gameplay.pushGuide', label: 'Push guide', hint: 'Auto shows it until Bean Level 5', options: [
          { value: 'auto', label: 'Auto' }, { value: 'on', label: 'On' }, { value: 'off', label: 'Off' },
        ],
      },
      { kind: 'toggle', path: 'gameplay.showDizzyNumbers', label: 'Dizzy numbers on nameplates' },
      { kind: 'toggle', path: 'gameplay.holdToCarry', label: 'Hold to carry', hint: 'Hold Grab to carry, release to throw' },
      {
        kind: 'choice', path: 'gameplay.aimAssist', label: 'Aim assist', options: [
          { value: 'off', label: 'Off' }, { value: 'low', label: 'Low' }, { value: 'high', label: 'High' },
        ],
      },
      { kind: 'toggle', path: 'gameplay.mouseAim', label: 'Mouse aim', hint: 'Throws, bombs and plungers fly toward the cursor' },
    ],
  },
  {
    id: 'controls', label: 'Controls', rows: [
      { kind: 'bindings', device: 'kbm', label: 'Keyboard & mouse' },
      { kind: 'action', id: 'resetBindings', label: 'Reset controls' },
      { kind: 'bindings', device: 'pad', label: 'Gamepad' },
      { kind: 'toggle', path: 'controls.pad.swapJumpGrab', label: 'Swap Jump / Grab' },
      { kind: 'slider', path: 'controls.pad.deadzone', label: 'Stick deadzone', min: 0.1, max: 0.3, step: 0.02, unit: '' },
      { kind: 'slider', path: 'controls.pad.rumble', label: 'Rumble', min: 0, max: 100, step: 10, unit: '%' },
      { kind: 'action', id: 'testRumble', label: 'Test rumble' },
      {
        kind: 'choice', path: 'controls.touch.layout', label: 'Touch layout', options: [
          { value: 'right', label: 'Right-handed' }, { value: 'left', label: 'Left-handed' },
        ],
      },
      {
        kind: 'choice', path: 'controls.touch.buttonSize', label: 'Button size', options: [
          { value: 'S', label: 'S' }, { value: 'M', label: 'M' }, { value: 'L', label: 'L' },
        ],
      },
      {
        kind: 'choice', path: 'controls.touch.stick', label: 'Move stick', options: [
          { value: 'floating', label: 'Floating' }, { value: 'fixed', label: 'Fixed' },
        ],
      },
      { kind: 'toggle', path: 'controls.touch.haptics', label: 'Touch haptics' },
    ],
  },
  {
    id: 'accessibility', label: 'Accessibility', rows: [
      { kind: 'toggle', path: 'accessibility.colorblind', label: 'Colorblind identity palette' },
      { kind: 'toggle', path: 'accessibility.highContrastOutlines', label: 'High-contrast outlines' },
      { kind: 'slider', path: 'accessibility.uiScale', label: 'UI scale', min: 80, max: 140, step: 5, unit: '%' },
      { kind: 'toggle', path: 'accessibility.captions', label: 'Sound captions', hint: 'Shows "Crowd: OOOH", "Bomb ticking", "Honk!"' },
    ],
  },
  {
    id: 'profile', label: 'Profile', rows: [
      { kind: 'action', id: 'editName', label: 'Change name' },
      { kind: 'action', id: 'resetProgress', label: 'Reset progress', danger: true, hint: 'Level, XP, Bucks, stats and purchases. Cannot be undone.' },
    ],
  },
];

/** Push guide visibility (§14.4): Auto shows it until Bean Level 5. */
export function pushGuideOn(s: Settings, beanLevel: number): boolean {
  return s.gameplay.pushGuide === 'on' || (s.gameplay.pushGuide === 'auto' && beanLevel < 5);
}

export interface SettingsStore {
  get(): Settings;
  set(path: SettingPath, value: SettingValue): Settings;
  patch(p: DeepPartial<Settings>): Settings;
  /** Replaces everything (e.g. a Settings screen that edits a copy). */
  replace(s: Settings): Settings;
  /** Restores the defaults of one group (or all). */
  reset(group?: keyof Settings): Settings;
}

/** The live settings: every change is sanitized, persisted and emitted on 'settings'. */
export function createSettingsStore(bus: { emit(k: 'settings', s: Settings): void }, initial: Settings = loadSettings()): SettingsStore {
  let current = sanitizeSettings(initial);
  const apply = (next: Settings): Settings => {
    if (JSON.stringify(next) === JSON.stringify(current)) return current;
    current = next;
    saveSettings(current);
    bus.emit('settings', current);
    return current;
  };
  return {
    get: () => current,
    set: (path, value) => apply(withSetting(current, path, value)),
    patch: (p) => apply(sanitizeSettings(merge(current, p))),
    replace: (s) => apply(sanitizeSettings(s)),
    reset(group) {
      if (!group) return apply(sanitizeSettings(DEFAULT_SETTINGS));
      return apply(sanitizeSettings({ ...current, [group]: JSON.parse(JSON.stringify(DEFAULT_SETTINGS[group])) as unknown }));
    },
  };
}
